/**
 * Ed Hardy Swim — "Complete the set".
 *
 * Sits inside the buy box, directly under Add to Cart. Finds every other piece
 * that shares this product's print and lets the customer build the full set in
 * one add. Most prints are a two-piece, but some run to three — MARY has a
 * bikini top, a bikini bottom and a mesh dress cover-up — so extra pieces are
 * offered as opt-in tiles after the main counterpart.
 *
 * Pairing comes from the product title, so nothing needs to be matched by hand.
 *
 * Discounting is honest by construction: with no code configured no saving is
 * claimed, and after adding, the code is applied and the cart re-read — if the
 * discount did not land, the saving is removed from the UI.
 */

import { loadCatalog, findCounterparts, samePrint, openCartDrawer } from '@eh/catalog';

const root = () => window.Shopify?.routes?.root || '/';

const money = (n) =>
  window.Shopify?.formatMoney
    ? window.Shopify.formatMoney(Math.round(n * 100))
    : `$${n.toFixed(2).replace(/\.00$/, '')}`;

const thumb = (src, w) =>
  src ? src.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, `_${w}x$1$3`) : '';

const esc = (s) => String(s).replace(/"/g, '&quot;');

/**
 * Live price for a specific variant. Product-level price uses whichever variant
 * was first in stock, so totals never moved when the shopper changed size.
 */
function variantMoney(product, variantId) {
  const vs = product.variants || [];
  const v =
    vs.find((x) => String(x.id) === String(variantId)) ||
    vs.find((x) => x.available) ||
    vs[0] ||
    {};
  const price = parseFloat(v.price != null ? v.price : product.price) || 0;
  const cmp = parseFloat(v.compare_at_price || 0) || 0;
  return { price, was: cmp > price ? cmp : price };
}

/** Human label for a piece, e.g. "bikini bottom", "mesh dress cover-up". */
const pieceName = (p) => (p.keyword ? p.keyword.toLowerCase() : p.title || 'item');

class EhBundle extends HTMLElement {
  async connectedCallback() {
    this.code = (this.dataset.code || '').trim();
    this.percent = parseFloat(this.dataset.percent || '10');
    this.ship = parseFloat(this.dataset.freeShipping || '75');
    this.heading = this.dataset.heading || 'COMPLETE THE SET';
    this.body = this.querySelector('[data-eh-body]');
    this.handle = this.dataset.handle;

    const catalog = await loadCatalog();
    const me = catalog.find((p) => p.handle === this.handle);
    if (!me || me.soldOut) return this.remove();

    /*
     * Two different offers, because they mean different things:
     *
     *   set   a bikini top or bottom has a literal other half. One counterpart
     *         leads, with a required size, and anything else in the print is an
     *         optional extra.
     *
     *   look  a cover-up or dress is not half of anything. Nothing is
     *         "completed" — the print's other pieces are simply offered as a
     *         look to wear it with, all optional.
     */
    const isHalfOfASet = me.cat === 'top' || me.cat === 'bottom';
    const counterparts = isHalfOfASet
      ? findCounterparts(me, catalog).filter((p) => !p.soldOut)
      : [];
    const extras = samePrint(me, catalog).filter(
      (p) => !p.soldOut && !counterparts.some((c) => c.handle === p.handle)
    );

    if (!counterparts.length && !extras.length) return this.remove();

    this.me = me;
    this.mode = counterparts.length ? 'set' : 'look';
    this.heading =
      this.mode === 'set' ? this.dataset.heading || 'COMPLETE THE SET' : this.dataset.lookHeading || 'WEAR IT WITH';

    this.options = counterparts;
    this.partner = counterparts[0] || null;
    this.extras = extras;
    this.size = null;
    /* handle -> chosen variant id for opted-in extras */
    this.picked = new Map();
    this.render();
  }

  mainVariantId() {
    const fromUrl = new URLSearchParams(location.search).get('variant');
    if (fromUrl) return fromUrl;
    const input = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]'
    );
    if (input && input.value) return input.value;
    const live = (this.me.variants || []).find((v) => v.available);
    return live ? String(live.id) : null;
  }

  mainVariant() {
    const id = this.mainVariantId();
    return (this.me.variants || []).find((v) => String(v.id) === String(id)) || null;
  }

  /** Sizes for a product, labelled from the Size option not the variant title. */
  sizeButtons(p, selectedId, attr) {
    const live = p.variants.filter((v) => v.available);
    if (!p.sized) return '';
    return `<div class="eh-bundle__sizes" role="group" aria-label="Size for ${esc(p.title)}">
      ${live
        .map(
          (v, i) =>
            `<button type="button" class="eh-bundle__size${
              String(selectedId) === String(v.id) ? ' is-active' : ''
            }" data-${attr}="${p.handle}" data-vid="${v.id}">${p.sizes[i] || v.title}</button>`
        )
        .join('')}
    </div>`;
  }

  addedPieces() {
    const out = [];
    if (this.partner) out.push({ p: this.partner, vid: this.size });
    this.extras.forEach((x) => {
      if (this.picked.has(x.handle)) out.push({ p: x, vid: this.picked.get(x.handle) });
    });
    return out;
  }

  render() {
    const myMoney = variantMoney(this.me, this.mainVariantId());
    const myPrice = myMoney.price;

    const pieces = this.addedPieces();
    const pieceMoney = pieces.map((x) => variantMoney(x.p, x.vid));
    const piecesTotal = pieceMoney.reduce((sum, m) => sum + m.price, 0);
    /* The set discount applies to the added pieces only, not to this product. */
    /*
     * Only discount when a code is actually wired up, and take the percentage
     * off the item the discount really targets.
     *
     * EHSET10 applies to bikini bottoms / boy shorts, so Shopify discounts the
     * BOTTOM whichever page you are on. Taking the percentage off "the added
     * piece" instead put the quote 20c out on a bottom's page ($70.20 shown vs
     * $70.40 charged). Match the rule, not the position.
     */
    const inSet = [{ p: this.me, m: myMoney }, ...pieces.map((x, n) => ({ p: x.p, m: pieceMoney[n] }))];
    const target =
      inSet.find((x) => x.p.cat === 'bottom') ||
      inSet.slice(1)[0] ||
      inSet[0];
    const saving = this.code && !this.suppressed ? (target.m.price * this.percent) / 100 : 0;

    /* Struck price is the genuine was-price, so the number next to it reads as
       a saving instead of an unexplained total. */
    const myWas = myMoney.was;
    const piecesWas = pieceMoney.reduce((sum, m) => sum + m.was, 0);
    const was = myWas + piecesWas;
    const total = myPrice + piecesTotal - saving;
    const off = was > total ? was - total : 0;
    const pct = was > 0 ? Math.round((off / was) * 100) : 0;

    this.body.innerHTML = `
      <p class="eh-bundle__heading">${this.heading}</p>

      ${
        this.partner
          ? `<div class="eh-bundle__row">
        <a class="eh-bundle__thumb" href="${root()}products/${this.partner.handle}">
          <img src="${thumb(this.partner.image, 200)}" alt="${esc(this.partner.title)}" loading="lazy" width="100" height="125">
        </a>
        <div class="eh-bundle__info">
          <p class="eh-bundle__kicker">Add the matching ${pieceName(this.partner)}</p>
          <p class="eh-bundle__name">${this.partner.title}</p>
          <p class="eh-bundle__price">
            ${(() => {
              const m = variantMoney(this.partner, this.size);
              return `${m.was > m.price ? `<s>${money(m.was)}</s> ` : ''}<strong>${money(m.price)}</strong>`;
            })()}
          </p>
          ${
            this.options.length > 1
              ? `<div class="eh-bundle__swaps">${this.options
                  .map(
                    (o, i) =>
                      `<button type="button" class="eh-bundle__swap${
                        o.handle === this.partner.handle ? ' is-active' : ''
                      }" data-swap="${i}">${pieceName(o)}</button>`
                  )
                  .join('')}</div>`
              : ''
          }
          ${this.sizeButtons(this.partner, this.size, 'partner')}
        </div>
      </div>`
          : ''
      }

      ${
        this.extras.length
          ? `<div class="eh-bundle__extras${this.partner ? '' : ' eh-bundle__extras--lead'}">
              <p class="eh-bundle__kicker">${
                this.partner ? 'Also in this print' : `The rest of the ${this.me.print.toLowerCase()} print`
              }</p>
              ${this.extras
                .map(
                  (x) => `
                <div class="eh-bundle__extra${this.picked.has(x.handle) ? ' is-on' : ''}">
                  <button type="button" class="eh-bundle__extra-head" data-extra="${x.handle}" aria-pressed="${this.picked.has(x.handle)}">
                    <img src="${thumb(x.image, 120)}" alt="" loading="lazy" width="40" height="50">
                    <span class="eh-bundle__extra-name">${pieceName(x)}</span>
                    <span class="eh-bundle__extra-price">${money(variantMoney(x, this.picked.get(x.handle)).price)}</span>
                    <span class="eh-bundle__extra-tick" aria-hidden="true">${this.picked.has(x.handle) ? '✓' : '+'}</span>
                  </button>
                  ${this.picked.has(x.handle) ? this.sizeButtons(x, this.picked.get(x.handle), 'extra') : ''}
                </div>`
                )
                .join('')}
             </div>`
          : ''
      }

      ${
        pieces.length
          ? `
      <div class="eh-bundle__foot">
        <dl class="eh-sum">
          ${
            was > myPrice + piecesTotal
              ? `<div><dt>Both pieces, full price</dt><dd><s>${money(was)}</s></dd></div>`
              : ''
          }
          <div><dt>Sale price</dt><dd>${money(myPrice + piecesTotal)}</dd></div>
          ${
            saving > 0
              ? `<div class="eh-sum__cut"><dt>Set discount (${this.percent}% off)</dt><dd>&minus;${money(saving)}</dd></div>`
              : ''
          }
          <div class="eh-sum__total"><dt>Total</dt><dd>${money(total)}</dd></div>
        </dl>
        ${
          off > 0
            ? `<p class="eh-sum__save">You save ${money(off)} &middot; ${pct}% off</p>`
            : ''
        }
        ${
          this.ship && total >= this.ship
            ? `<p class="ehbb__ship is-on"><span class="ehbb__ship-tick" aria-hidden="true">\u2713</span> Free shipping unlocked</p>`
            : ''
        }
        <button type="button" class="eh-bundle__add" data-eh-add>
          ${this.mode === 'set' ? 'Add set to bag' : 'Add both to bag'} &middot; ${money(total)}
        </button>
      </div>
      <p class="eh-bundle__msg" data-eh-msg role="status" aria-live="polite"></p>`
          : `<p class="eh-bundle__hint">Pick a piece above to add it with this one.</p>`
      }
    `;

    this.body.querySelectorAll('[data-partner]').forEach((b) =>
      b.addEventListener('click', () => {
        this.size = b.dataset.vid;
        this.render();
      })
    );
    this.body.querySelectorAll('[data-extra][data-vid]').forEach((b) =>
      b.addEventListener('click', () => {
        this.picked.set(b.dataset.extra, b.dataset.vid);
        this.render();
      })
    );
    this.body.querySelectorAll('[data-extra]:not([data-vid])').forEach((b) =>
      b.addEventListener('click', () => {
        const h = b.dataset.extra;
        if (this.picked.has(h)) this.picked.delete(h);
        else {
          const x = this.extras.find((e) => e.handle === h);
          const live = x.variants.filter((v) => v.available);
          this.picked.set(h, x.sized ? null : String(live[0].id));
        }
        this.render();
      })
    );
    this.body.querySelectorAll('[data-swap]').forEach((b) =>
      b.addEventListener('click', () => {
        this.partner = this.options[Number(b.dataset.swap)];
        this.size = null;
        this.render();
      })
    );
    const addBtn = this.body.querySelector('[data-eh-add]');
    if (addBtn) addBtn.addEventListener('click', () => this.add());
    this.watchMainSize();
  }

  watchMainSize() {
    const label = this.body.querySelector('[data-eh-mine]');
    if (!label) return;
    const paint = () => {
      const v = this.mainVariant();
      label.textContent = v && v.title && v.title !== 'Default Title' ? v.title : '—';
    };
    paint();
    if (this._watching) return;
    this._watching = true;
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form) form.addEventListener('change', () => setTimeout(paint, 60));
    document.addEventListener('variant:update', paint);
    window.addEventListener('popstate', paint);
    this._poll = setInterval(paint, 700);
  }

  disconnectedCallback() {
    if (this._poll) clearInterval(this._poll);
  }

  msg(text, isError) {
    const el = this.body.querySelector('[data-eh-msg]');
    if (!el) return;
    el.textContent = text || '';
    el.classList.toggle('is-error', !!isError);
  }

  async add() {
    const btn = this.body.querySelector('[data-eh-add]');
    const mineId = this.mainVariantId();
    if (!mineId) return this.msg('Choose your size above first.', true);

    const items = [{ id: Number(mineId), quantity: 1 }];

    for (const { p, vid } of this.addedPieces()) {
      const live = p.variants.filter((v) => v.available);
      const chosen = vid || (p.sized && live.length > 1 ? null : String((live[0] || {}).id));
      if (!chosen) {
        return this.msg(`Pick a size for the ${pieceName(p)}.`, true);
      }
      items.push({ id: Number(chosen), quantity: 1 });
    }

    btn.disabled = true;
    const original = btn.textContent;
    btn.textContent = 'Adding…';

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add to cart.');
      }

      let discounted = false;
      if (this.code) {
        /*
         * A code from an earlier bundle survives /cart/clear.js and Shopify
         * will not let a weaker one replace it, so a shopper who built a dress
         * bundle first then added a set was quoted the set price and charged
         * without the set discount. Clear whatever is attached first, then
         * confirm the code we quoted is the code that landed — "some discount
         * happened" is not the same thing.
         */
        await fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
        }).catch(() => {});

        await fetch(`${root()}discount/${encodeURIComponent(this.code)}`, {
          credentials: 'same-origin',
        }).catch(() => {});

        const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
        const code = this.code.toUpperCase();
        discounted = (cart.items || []).some((i) =>
          (i.discounts || []).some((d) => String(d.title).toUpperCase() === code)
        );
        if (!discounted) {
          /* the code did not apply — withdraw the claim rather than lie */
          this.suppressed = true;
          this.render();
        }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
      openCartDrawer();
      this.msg(discounted ? 'Set added. Discount applied at checkout.' : 'Set added to your bag.');
      btn.textContent = 'Added';
      setTimeout(() => {
        btn.textContent = original;
        btn.disabled = false;
      }, 1800);
    } catch (e) {
      this.msg(e.message || 'Something went wrong.', true);
      btn.textContent = original;
      btn.disabled = false;
    }
  }
}

if (!customElements.get('eh-bundle')) customElements.define('eh-bundle', EhBundle);
